import React from "react";
import { MediaValue, detectMedia } from "../diff/MediaValue";

/**
 * Journal des changements d'une entrée d'audit : rend la property `changes` (JSON borné écrit par
 * le backend à chaque save) en lignes lisibles et colorées : « Titre : avant devient après »,
 * « Bloc Galerie retiré », sans recharger les versions. Le diff COMPLET reste accessible via le
 * bouton dédié (versionId / prevVersionId).
 */

export type ChangeEntry =
	| { kind: "scalar"; field: string; op: string; before?: string; after?: string }
	| { kind: "block"; field: string; block: string; op: string }
	| { kind: "truncated"; dropped: number };

/** Parse borné et tolérant : un JSON invalide ou une forme inattendue rend null (pas de crash de ligne). */
export const parseChangeJournal = (raw?: string | null): ChangeEntry[] | null => {
	if (!raw || raw.trim().length === 0) return null;
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return null;
	}
	if (!Array.isArray(parsed)) return null;

	const entries: ChangeEntry[] = [];
	for (const item of parsed) {
		if (!item || typeof item !== "object") continue;
		const obj = item as Record<string, unknown>;
		if (obj.truncated === true) {
			entries.push({ kind: "truncated", dropped: typeof obj.dropped === "number" ? obj.dropped : 0 });
			continue;
		}
		const field = typeof obj.field === "string" ? obj.field : null;
		const op = typeof obj.op === "string" ? obj.op : null;
		if (!field || !op) continue;
		if (typeof obj.block === "string") {
			entries.push({ kind: "block", field, block: obj.block, op });
		} else {
			entries.push({
				kind: "scalar",
				field,
				op,
				before: typeof obj.before === "string" ? obj.before : undefined,
				after: typeof obj.after === "string" ? obj.after : undefined
			});
		}
	}
	return entries.length > 0 ? entries : null;
};

const OP_META: Record<string, { label: string; chip: string }> = {
	added: { label: "Ajouté", chip: "border-emerald-200 bg-emerald-50 text-emerald-700" },
	removed: { label: "Retiré", chip: "border-rose-200 bg-rose-50 text-rose-700" },
	modified: { label: "Modifié", chip: "border-amber-200 bg-amber-50 text-amber-700" },
	moved: { label: "Déplacé", chip: "border-sky-200 bg-sky-50 text-sky-700" }
};

const opMeta = (op: string) => OP_META[op] ?? { label: op, chip: "border-slate-200 bg-slate-100 text-slate-600" };

const OpChip: React.FC<{ op: string }> = ({ op }) => {
	const meta = opMeta(op);
	return (
		<span className={`inline-flex shrink-0 items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${meta.chip}`}>
			{meta.label}
		</span>
	);
};

const Value: React.FC<{ tone: "before" | "after"; children: string }> = ({ tone, children }) => {
	// Une valeur média se voit DIRECTEMENT : vignette pour une image, bouton d'aperçu pour un
	// document, la modale s'ouvre au clic. Le texte barré/vert reste pour tout le reste.
	const media = detectMedia(children);
	if (media) {
		return (
			<span className={tone === "before" ? "opacity-60" : undefined} title={tone === "before" ? "Valeur précédente" : undefined}>
				<MediaValue media={media} compact />
			</span>
		);
	}
	return (
		<span
			className={
				tone === "before"
					? "rounded bg-rose-50 px-1 py-0.5 font-mono text-[11px] text-rose-700 line-through decoration-rose-300"
					: "rounded bg-emerald-50 px-1 py-0.5 font-mono text-[11px] text-emerald-700"
			}
		>
			{children === "" ? "(vide)" : children}
		</span>
	);
};

export interface ChangeJournalProps {
	/** Le JSON brut de la property `changes` de l'entrée du journal. */
	changesJson?: string | null;
}

export const ChangeJournal: React.FC<ChangeJournalProps> = ({ changesJson }) => {
	const entries = parseChangeJournal(changesJson);
	if (!entries) return null;

	return (
		<ul className="space-y-1.5" data-testid="change-journal">
			{entries.map((entry, index) => {
				if (entry.kind === "truncated") {
					return (
						<li key={`trunc-${index}`} className="text-xs italic text-slate-500">
							{entry.dropped > 0
								? `${entry.dropped} changement${entry.dropped > 1 ? "s" : ""} de plus non détaillé${entry.dropped > 1 ? "s" : ""} (journal borné) ; le diff complet les montre tous.`
								: "Journal tronqué ; le diff complet montre tout."}
						</li>
					);
				}
				if (entry.kind === "block") {
					return (
						<li key={`${entry.field}-${entry.block}-${index}`} className="flex flex-wrap items-center gap-2 text-xs text-slate-700">
							<OpChip op={entry.op} />
							<span>
								Bloc <span className="font-semibold">{entry.block}</span>
								<span className="text-slate-400"> ({entry.field})</span>
							</span>
						</li>
					);
				}
				return (
					<li key={`${entry.field}-${index}`} className="flex flex-wrap items-center gap-2 text-xs text-slate-700">
						<OpChip op={entry.op} />
						<span className="font-semibold">{entry.field}</span>
						{entry.before !== undefined && <Value tone="before">{entry.before}</Value>}
						{entry.before !== undefined && entry.after !== undefined && <span className="text-slate-400">devient</span>}
						{entry.after !== undefined && <Value tone="after">{entry.after}</Value>}
					</li>
				);
			})}
		</ul>
	);
};

export default ChangeJournal;
